import React, { Component } from 'react'
import Security from '../../commons/security/Security.js'
import AjaxHandler from '../../commons/ajax/AjaxHandler.js'
import Loading from '../ui/Loading.js'
import ConfigBusiness from '../../commons/config/ConfigBusiness.js'
import moment from 'moment'
import 'moment/min/locales'
import {FormattedMessage, injectIntl} from 'react-intl';
import { tieneFaltante } from '../inventario/solicitudStockFlow.js'

/** El mismo valor que `Item::MANO_DE_OBRA` del backend. Todo lo demás es repuesto. */
const MANO_DE_OBRA = 'Mano De Obra';

/** `ItemController::MAX_ITEMS_STOCK_DISPONIBLE`: más ids que eso y el endpoint responde 400. */
const MAX_ITEMS_STOCK = 200;

class TicketItems extends Component {
	constructor(props) {
    super(props);
    this.props = props;

    this.ajaxHandler = new AjaxHandler();

    moment.locale('es');

    if(props.ticket) {
      this.ticket = props.ticket;
    }


    this.state = {
      loading: false,
      items: [],
      itemsCierre: [],
      // VEC-3320 — itemId => disponible en la base del móvil. Vacío mientras no se sepa, que es
      // distinto de "cero": con el mapa vacío la columna muestra '-' y no un faltante inventado.
      stockPorItem: {}
    }
  }

  inventarioHabilitado() {
    return ConfigBusiness.get('inventarioItems.habilitado') === 'true';
  }

  /** El móvil del ticket, que es lo que define contra qué base se mira el stock. */
  movilDelTicket() {
    const movil = this.ticket ? this.ticket.movil : null;

    if (!movil) return null;

    return movil.id ? movil.id : movil;
  }

  /**
   * VEC-3320 Sc. 01 — el disponible de cada repuesto del ticket.
   *
   * Mismo endpoint que usa el presupuesto detallado (`TicketPresupuestoDetallado.js:146`), así que
   * no hay backend nuevo: devuelve el mapa `itemId => disponible` y ya trae adentro el guard del
   * módulo apagado.
   *
   * Sin móvil no se consulta nada: un ticket de llanta o de persona no tiene base contra la cual
   * mirar, y ahí el disponible es "no se puede saber" y no "cero".
   *
   * Un fallo NO puede romper la pantalla: el detalle del ticket se tiene que poder ver igual. Sin
   * mapa, la columna queda en '-'.
   */
  cargarStockDisponible(items) {
    if (!this.inventarioHabilitado()) return;

    const movilId = this.movilDelTicket();

    if (!movilId) return;

    // El predicado es "no es mano de obra", igual que `Item::esManoDeObra()` en el backend, y no
    // `tipo === 'Producto'`: `items.tipo` es un varchar libre y cualquier valor que no sea esos dos
    // caería en '-' en silencio.
    // Los ids se deduplican antes de lotear: un ticket puede traer dos líneas del mismo repuesto, y
    // el backend aplica `array_unique` ANTES del tope (ItemController.php:228), así que el límite
    // real es de ids ÚNICOS. Sin esto se podían mandar dos requests donde el backend aceptaba uno.
    const ids = Array.from(new Set(
      (items || [])
        .filter(function(item) { return item.tipo !== MANO_DE_OBRA; })
        .map(function(item) { return item.id; })
    ));

    if (ids.length === 0) return;

    // POR LOTES, NO TRUNCANDO. El endpoint rechaza con un 400 más de 200 ids de una
    // (ItemController::MAX_ITEMS_STOCK_DISPONIBLE). Quedarse con los primeros 200 dejaba la columna
    // en '-' para los repuestos del final sin decir por qué, y el escenario pide el disponible de
    // CADA repuesto del ticket. Los mapas parciales se fusionan porque vienen indexados por id.
    const lotes = [];

    for (let i = 0; i < ids.length; i += MAX_ITEMS_STOCK) {
      lotes.push(ids.slice(i, i + MAX_ITEMS_STOCK));
    }

    // `ticket=` hace que el backend le SUME al disponible lo que este ticket ya tiene comprometido.
    // Sin eso, un ticket ya presupuestado ve su propio stock como faltante: los envases están en
    // RESERVADO a nombre suyo, el depósito devuelve 0 y la celda pinta en rojo algo que el ticket
    // ya tiene tomado. Es el mismo ajuste que hace el presupuesto detallado con `presupuesto=`
    // (TicketPresupuestoDetallado.js:142), en el eje del ticket porque acá no hay un presupuesto.
    //
    // `silent: true` y no `getJson()`: el handler genérico abre el modal de "Error interno del
    // sistema" para todo status >= 404 y, a diferencia del 403, NO deduplica (AjaxHandler.js:274).
    // Con varios lotes eso serían varios modales apilados sobre el detalle del ticket por una
    // columna accesoria. Acá el fallo se absorbe: es lo que promete el comentario de arriba.
    const consultar = (lote) => this.ajaxHandler.getJsonOptions(
      '/items/stock-disponible?movil=' + encodeURIComponent(movilId)
        + '&ticket=' + encodeURIComponent(this.ticket.id)
        + '&items=' + lote.join(','),
      { method: 'GET', silent: true }
    );

    // CADA LOTE ABSORBE SU PROPIO FALLO. Con un `Promise.all` pelado, que fallara uno descartaba
    // también los lotes que sí habían respondido y dejaba la columna entera en '-'. Así se pinta lo
    // que se pudo traer y el resto queda en '-', que ya significa "no se puede saber".
    //
    // El `.catch` por promesa en vez de `Promise.allSettled`: este bundle no lo polyfillea y no vale
    // la pena arrastrarlo por esto.
    Promise.all(lotes.map((lote) => consultar(lote).catch(() => ({}))))
      .then((mapas) => this.setState({
        stockPorItem: Object.assign({}, ...mapas.map((mapa) => mapa || {}))
      }));
  }

  /**
   * La celda de disponibilidad de un repuesto (VEC-3320 Sc. 01 y 02).
   *
   * El faltante se decide con `tieneFaltante()` —el mismo predicado que usan el alta del correctivo
   * y el presupuesto— y no con una comparación propia: son tres pantallas diciendo lo mismo, y el
   * caso del `null` ("no se puede saber", que NO es faltante) es fácil de perder al reescribirlo.
   */
  celdaStockDisponible(item) {
    const disponible = this.state.stockPorItem[String(item.id)];

    if (item.tipo === MANO_DE_OBRA || disponible === null || disponible === undefined) {
      return <span className="text-muted">-</span>;
    }

    const falta = tieneFaltante({ cantidad: item.cantidad, stockDisponible: disponible });

    return (
      <span className={'badge ' + (falta ? 'badge-danger' : 'badge-success')}>{disponible}</span>
    );
  }

  componentDidMount(nextProps, nextState) {
    if(Security.hasPermission('TICKETS_VER_TAREAS')) {
        this.ajaxHandler.subscribe(this);
        this.loadFormData();
    } else {
      this.setState({
        redirectTo: '/error'
      });
    }
  }

  componentWillUnmount() {
    this.ajaxHandler.unsubscribe();
  }

  loadFormData() {
    this.ajaxHandler.getJson('/items/ticket-has-items-cierre/' + this.ticket.id)
    .then(hasItemsCierre => {
      let component = this;
      // let grid = ConfigBusiness.get('tickets.tareasCierre.habilitado') === 'true' ? ( hasTareasCierre ? '/gridCierre' : '/grid' ) : '/grid';
      let gridCierre = hasItemsCierre ? '/gridCierre' : '/grid';
      this.setState({ loading: true });
      return new Promise((resolve, reject)=>{
        Promise.all([
      this.ajaxHandler.getJson('/items/ticket/' + this.ticket.id + '/grid' ),
      this.ajaxHandler.getJson('/items/ticket/' + this.ticket.id + gridCierre )

        ]).then(data => {
        if(data) {
          component.setState({
            items: data[0],
            itemsCierre: data[1]
          });
          component.cargarStockDisponible(data[0]);
        }
      })
      .catch(function(error) {
        component.ajaxHandler.handleError(error);
        reject();
    }).finally(() => {
      component.setState({ loading: false });
      resolve();
    });
  });
  });
  }

	render() {
    if(this.ticket) {
      // Determinar si usar items de cierre según configuraciones (global o con abono)
      let usarItemsCierre = ConfigBusiness.get('tickets.tareasCierre.habilitado') === 'true';
      let usarItemsCierreConAbono = false;
      if (!usarItemsCierre && ConfigBusiness.get('tickets.conAbono.tareasCierre.habilitado') === 'true') {
        usarItemsCierreConAbono = this.props.ticket.abono === true;
        usarItemsCierre = usarItemsCierreConAbono;
      }

      // Mostrar montos SOLO si es config global Y montosRepuesto está habilitado
      // Si es config de abono, NO mostrar montos (funciona como montosRepuesto en false)
      let mostrarMontos = !usarItemsCierreConAbono && ConfigBusiness.get('tickets.tareasCierre.montosRepuesto') === 'true';

      var tipoFiltro = this.props.tipoFiltro || null;
      // VEC-3320 — la columna de stock solo tiene sentido para repuestos y con el módulo de
      // Inventario prendido. Sin móvil no se consultó nada, así que tampoco se muestra.
      var mostrarStock = this.inventarioHabilitado()
        && tipoFiltro !== 'Mano De Obra'
        && !!this.movilDelTicket();
      var trabajaConManoDeObra = ConfigBusiness.get('tickets.trabajaConManoDeObra.habilitado') === 'true';
      var itemsFiltrados = tipoFiltro
        ? this.state.items.filter(function(item) { return item.tipo === tipoFiltro; })
        : this.state.items;
      var itemsCierreFiltrados = (tipoFiltro && trabajaConManoDeObra)
        ? this.state.itemsCierre.filter(function(item) { return item.tipo === tipoFiltro; })
        : this.state.itemsCierre;

      return (
        <React.Fragment>
          {this.state.loading && <Loading />}
          {this.state.items ? (
            <div className="card pull-up">
              <div className="card-content">
              { usarItemsCierre && (this.props.ticket.estado === 'CONFIRMAR_REPARACION' || this.props.ticket.estado === 'LISTO_PARA_RETIRAR' || this.props.ticket.estado === 'CERRADO'  ) ? (
                <div className="card-body">
                  <h4 className="form-section">
                  <i className="la la-tasks"></i>
                  {tipoFiltro === 'Mano De Obra'
                    ? <FormattedMessage id="ticketItems.render.task.header_items_mdo_cierre" defaultMessage=" Mano de Obra Realizados (Cierre)"/>
                    : <FormattedMessage id="ticketItems.render.task.header_items_cierre" defaultMessage=" Repuestos Realizados (Cierre)"/>
                  }
                  </h4>
                  <div className="row">
                    <div className="col-md-12">
                      <table className="table">
                        <thead className="thead-fleet">
                          <tr>
                            <th scope="col"><FormattedMessage id="ticketItems.render.task.table_column_servicio" defaultMessage="Servicio"/></th>
                            <th scope="col">
                              {tipoFiltro === 'Mano De Obra'
                                ? <FormattedMessage id="ticketItems.render.task.table_column_mdo" defaultMessage="Mano de Obra"/>
                                : <FormattedMessage id="ticketItems.render.task.table_column_items" defaultMessage="Repuestos"/>
                              }
                            </th>
                            { mostrarMontos ? (<th scope="col"><FormattedMessage id="ticketItems.render.task.table_column_monto_total" defaultMessage="Monto Total"/></th>) :''}
                          </tr>
                        </thead>
                        <tbody>
                        {itemsCierreFiltrados.map((itemCierre, index)=>{
                          return(
                            <tr key={index}>
                              <td className="align-middle">{itemCierre.servicio.nombre}</td>
                              <td className="align-middle">{itemCierre.nombre}</td>
                              { mostrarMontos ? (<td className="align-middle">{itemCierre.montoTotal}</td>) :''}
                            </tr>
                          );
                        })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                ) : ''}

                <div className="card-body">
                  <h4 className="form-section">
                   <i className="la la-tasks"></i>
                   {tipoFiltro === 'Mano De Obra'
                     ? <FormattedMessage id="ticketItems.render.task.header_items_mdo_apertura" defaultMessage=" Mano de Obra indicados al momento de apertura del Ticket"/>
                     : <FormattedMessage id="ticketItems.render.task.header_items_apertura" defaultMessage=" Repuestos indicados al momento de apertura del Ticket"/>
                   }
                  </h4>
                  <div className="row">
                    <div className="col-md-12">
                      <table className="table">
                        <thead className="thead-fleet">
                          <tr>
                            <th scope="col"><FormattedMessage id="ticketItems.render.task.table_column_servicio" defaultMessage="Servicio"/></th>
                            <th scope="col">
                              {tipoFiltro === 'Mano De Obra'
                                ? <FormattedMessage id="ticketItems.render.task.table_column_mdo" defaultMessage="Mano de Obra"/>
                                : <FormattedMessage id="ticketItems.render.task.table_column_items" defaultMessage="Repuestos"/>
                              }
                            </th>
                            { mostrarStock ? (<th scope="col"><FormattedMessage id="ticketItems.render.task.table_column_cantidad" defaultMessage="Cantidad"/></th>) : '' }
                            { mostrarStock ? (<th scope="col"><FormattedMessage id="ticketItems.render.task.table_column_disponible" defaultMessage="Disponible"/></th>) : '' }
                          </tr>
                        </thead>
                        <tbody>
                        {itemsFiltrados.map((item, index)=>{
                          return(
                            <tr key={index}>
                              <td className="align-middle">{item.servicio.nombre}</td>
                              <td className="align-middle">{item.nombre}</td>
                              { mostrarStock ? (<td className="align-middle">{item.cantidad}</td>) : '' }
                              { mostrarStock ? (<td className="align-middle">{this.celdaStockDisponible(item)}</td>) : '' }
                            </tr>
                          );
                        })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : ( null )}
        </React.Fragment>
      )
    } else {
      return null;
    }
  }
}

export default injectIntl(TicketItems);
